import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { detectRecurring, getManagedRecurring, createRecurring, deleteRecurring } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowClockwise, Plus, Trash, Check, Warning } from '@phosphor-icons/react';

const freqColors = {
  weekly: 'bg-blue-50 text-blue-700 border-blue-500',
  biweekly: 'bg-cyan-50 text-cyan-700 border-cyan-500',
  monthly: 'bg-emerald-50 text-emerald-700 border-emerald-500',
  quarterly: 'bg-amber-50 text-amber-700 border-amber-500',
  annual: 'bg-violet-50 text-violet-700 border-violet-500'
};

export default function RecurringPage() {
  const [detected, setDetected] = useState([]);
  const [managed, setManaged] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [form, setForm] = useState({ vendor_name: '', expected_amount: '', frequency: 'monthly', notes: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [detRes, manRes] = await Promise.all([detectRecurring(), getManagedRecurring()]);
      setDetected(detRes.data.recurring_patterns);
      setManaged(manRes.data.recurring);
    } catch (error) {
      toast.error('Failed to load recurring data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddManaged = async (e) => {
    e.preventDefault();
    try {
      await createRecurring({ ...form, expected_amount: parseFloat(form.expected_amount) });
      toast.success('Recurring expectation added');
      setIsDialogOpen(false);
      setForm({ vendor_name: '', expected_amount: '', frequency: 'monthly', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add');
    }
  };

  const handlePromoteDetected = async (pattern) => {
    try {
      await createRecurring({
        vendor_name: pattern.vendor_name,
        expected_amount: pattern.avg_amount,
        frequency: pattern.frequency,
        notes: `Auto-detected: ${pattern.occurrence_count} occurrences, avg gap ${pattern.avg_gap_days} days`
      });
      toast.success(`Added "${pattern.vendor_name}" as managed recurring`);
      fetchData();
    } catch (error) {
      toast.error('Failed to add');
    }
  };

  const handleDelete = async (recId) => {
    try {
      await deleteRecurring(recId);
      toast.success('Removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <Layout title="Recurring Transactions">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Recurring Transactions">
      <div data-testid="recurring-page" className="space-y-6">
        <Tabs defaultValue="detected">
          <div className="flex items-center justify-between">
            <TabsList className="bg-zinc-100 p-1 rounded-sm">
              <TabsTrigger value="detected" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-detected">
                <ArrowClockwise size={14} className="mr-1.5" /> Auto-Detected ({detected.length})
              </TabsTrigger>
              <TabsTrigger value="managed" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-managed">
                <Check size={14} className="mr-1.5" /> Managed ({managed.length})
              </TabsTrigger>
            </TabsList>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-zinc-950 text-white rounded-sm" data-testid="add-recurring-button">
                  <Plus size={16} className="mr-2" /> Add Recurring
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="recurring-dialog">
                <DialogHeader><DialogTitle>Add Recurring Expectation</DialogTitle></DialogHeader>
                <form onSubmit={handleAddManaged} className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Vendor</Label>
                    <Input value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} required placeholder="e.g., AWS" className="rounded-sm" data-testid="rec-vendor-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-widest text-zinc-500">Amount ($)</Label>
                      <Input type="number" step="0.01" value={form.expected_amount} onChange={(e) => setForm({ ...form, expected_amount: e.target.value })} required className="rounded-sm font-mono" data-testid="rec-amount-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-widest text-zinc-500">Frequency</Label>
                      <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                        <SelectTrigger className="rounded-sm" data-testid="rec-freq-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="biweekly">Biweekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="annual">Annual</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">Cancel</Button>
                    <Button type="submit" className="bg-zinc-950 text-white rounded-sm" data-testid="save-recurring-button">Save</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Auto-Detected */}
          <TabsContent value="detected" className="mt-6">
            {detected.length === 0 ? (
              <div className="bg-white border border-zinc-200 rounded-sm p-12 text-center">
                <ArrowClockwise size={48} className="mx-auto mb-4 text-zinc-300" />
                <p className="font-medium mb-1">No recurring patterns detected</p>
                <p className="text-sm text-zinc-500">Add more transactions to enable pattern detection.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {detected.map((p, i) => (
                  <div key={`${p.vendor_name}-${p.frequency}-${i}`} className="bg-white border border-zinc-200 rounded-sm p-5" data-testid={`detected-pattern-${i}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-medium">{p.vendor_name}</h3>
                        <p className="text-xs text-zinc-500">{p.occurrence_count} occurrences, avg {p.avg_gap_days} days apart</p>
                      </div>
                      <span className={`px-2 py-1 text-xs uppercase tracking-wider border rounded-sm ${freqColors[p.frequency] || 'bg-zinc-100 text-zinc-600 border-zinc-300'}`}>
                        {p.frequency}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-medium">${p.avg_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      <Button variant="outline" size="sm" onClick={() => handlePromoteDetected(p)} className="rounded-sm" data-testid={`promote-${i}`}>
                        <Plus size={14} className="mr-1" /> Track
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Managed */}
          <TabsContent value="managed" className="mt-6">
            {managed.length === 0 ? (
              <div className="bg-white border border-zinc-200 rounded-sm p-12 text-center">
                <Check size={48} className="mx-auto mb-4 text-zinc-300" />
                <p className="font-medium mb-1">No managed recurring transactions</p>
                <p className="text-sm text-zinc-500">Promote detected patterns or add manually.</p>
              </div>
            ) : (
              <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
                <table className="w-full table-dense">
                  <thead>
                    <tr className="text-left">
                      <th className="px-6 pt-4">Vendor</th>
                      <th className="px-6 pt-4 text-right">Amount</th>
                      <th className="px-6 pt-4">Frequency</th>
                      <th className="px-6 pt-4">Notes</th>
                      <th className="px-6 pt-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {managed.map((r) => (
                      <tr key={r.rec_id} className="hover:bg-zinc-50">
                        <td className="px-6 text-sm font-medium">{r.vendor_name}</td>
                        <td className="px-6 text-right font-mono text-sm">${r.expected_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        <td className="px-6"><span className={`px-2 py-1 text-xs uppercase border rounded-sm ${freqColors[r.frequency] || 'bg-zinc-100 text-zinc-600'}`}>{r.frequency}</span></td>
                        <td className="px-6 text-sm text-zinc-500 truncate max-w-xs">{r.notes || '—'}</td>
                        <td className="px-6">
                          <button onClick={() => handleDelete(r.rec_id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-sm">
                            <Trash size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
